import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, HandCoins, Landmark, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { KIND_LABEL, rateText, summaryExcel, summaryPdf } from "@/lib/payoutExport";
import { Cards, RangeBar, defaultRange, fmtD, inr } from "@/components/payout/PayoutBits";
import { DownloadMenu } from "@/components/payout/DownloadMenu";
import { PersonForm } from "@/components/payout/PayoutForms";
import { PersonPanel } from "@/components/payout/PersonPanel";
import { BulkOpeningForm } from "@/components/payout/OpeningForms";

const TABS = ["booker", "salesman", "developer"];
const NOTE = {
  booker: "Booker earnings are entered manually (Qty × Rate).",
  salesman: "Qty is automatic: FRESH bookings of assigned agencies × rate.",
  developer: "Qty is automatic: FRESH bookings of all agencies, separate Indane / Bharat / HP rates.",
};

function PeopleTable({ kind, data, onOpen }) {
  if (!data.people.length) return <div className="text-sm text-[#6B6B61] py-10 text-center" data-testid="payout-people-empty">No {KIND_LABEL[kind]} yet. Create a user with the {KIND_LABEL[kind]} role in Admin → Users (it appears here automatically){kind === "booker" ? ', or pick a name with "+ Booker"' : ""}.</div>;
  return (
    <div className="overflow-x-auto border border-[#EDEDE5] rounded-lg bg-white">
      <table className="w-full text-sm" data-testid="payout-people-table">
        <thead className="bg-[#F5F5F0] text-[10px] font-mono uppercase text-[#6B6B61]">
          <tr>{["Name", kind !== "booker" && "Rate", kind === "salesman" && "Agencies", "Opening", "Qty", "Earned", "Bonus", "Paid", "Deduction", "Balance"].filter(Boolean).map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
        </thead>
        <tbody>
          {data.people.map((p) => (
            <tr key={p.id} onClick={() => onOpen(p.id)} className="border-t border-[#F0F0EA] cursor-pointer hover:bg-[#FAFAF6]" data-testid={`payout-person-${p.id}`}>
              <td className="px-3 py-2 font-semibold">{p.name}</td>
              {kind !== "booker" && <td className="px-3 py-2 font-mono text-xs">{rateText(p)}</td>}
              {kind === "salesman" && <td className="px-3 py-2 text-xs max-w-[240px] truncate" title={p.agency_names.join(", ")}>{p.agency_names.length} · {p.agency_names.join(", ")}</td>}
              <td className="px-3 py-2 whitespace-nowrap" data-testid={`payout-person-opening-${p.id}`}>
                {inr(p.opening)}{p.opening_balance && <span className="block text-[10px] text-[#B7791F]">from {fmtD(p.opening_balance.date)}</span>}
              </td>
              <td className="px-3 py-2">{p.qty}</td>
              <td className="px-3 py-2">{inr(p.earned)}</td>
              <td className="px-3 py-2">{inr(p.plus)}</td>
              <td className="px-3 py-2">{inr(p.paid)}</td>
              <td className="px-3 py-2">{inr(p.minus)}</td>
              <td className={`px-3 py-2 font-semibold ${p.balance < 0 ? "text-[#C8553D]" : ""}`} data-testid={`payout-person-balance-${p.id}`}>{inr(p.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PayoutManager() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("booker");
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    let alive = true;
    api.get("/payouts/people", { params: { kind: tab, from_date: range.from, to_date: range.to } })
      .then(({ data }) => alive && setData({ kind: tab, ...data })).catch((e) => toast.error(formatApiError(e)));
    return () => { alive = false; };
  }, [tab, range.from, range.to]);
  useEffect(() => { setData(null); return load(); }, [load]);
  const ready = data && data.kind === tab;
  const full = async (r) => (await api.get("/payouts/people", { params: { kind: tab, from_date: r.from, to_date: r.to, with_rows: true } })).data;
  return (
    <div className="min-h-screen bg-[#F8F8F3]">
      <header className="border-b border-[#EDEDE5] bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/home")} className="w-9 h-9 rounded flex items-center justify-center hover:bg-[#F0F0EA]" data-testid="payout-back"><ArrowLeft className="w-4 h-4" /></button>
          <HandCoins className="w-5 h-5 text-[#385C4B]" />
          <h1 className="font-semibold text-lg tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Payout Manager</h1>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex gap-1">
          {TABS.map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`payout-tab-${k}`}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === k ? "border-[#385C4B] text-[#385C4B] font-semibold" : "border-transparent text-[#6B6B61] hover:text-[#1C1C19]"}`}>
              {KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <RangeBar range={range} setRange={setRange} />
          <div className="flex gap-2">
            {ready && <DownloadMenu prefix="payout-summary" fetcher={full} onExcel={(d, f, t) => summaryExcel(tab, d, f, t)} onPdf={(d, f, t) => summaryPdf(tab, d, f, t)} />}
            {ready && data.people.length > 0 && (
              <button onClick={() => setBulkOpen(true)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded border border-[#B7791F] text-[#B7791F] hover:bg-[#FFF8E6]" data-testid="payout-bulk-opening">
                <Landmark className="w-3.5 h-3.5" />Opening Balance
              </button>
            )}
            <button onClick={() => setAdding(true)} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded bg-[#385C4B] text-white hover:bg-[#2c4a3c]" data-testid="payout-add-person">
              <Plus className="w-3.5 h-3.5" />{KIND_LABEL[tab]}
            </button>
          </div>
        </div>
        <div className="text-xs text-[#6B6B61]" data-testid="payout-tab-note">{NOTE[tab]}</div>
        {!ready ? <Loader2 className="w-6 h-6 animate-spin text-[#385C4B] mx-auto my-10" /> : (
          <>
            <Cards s={data.totals} showOpening />
            <PeopleTable kind={tab} data={data} onOpen={setOpenId} />
          </>
        )}
      </main>
      {adding && <PersonForm kind={tab} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {bulkOpen && <BulkOpeningForm kind={tab} people={data.people} onClose={() => setBulkOpen(false)} onSaved={() => { setBulkOpen(false); load(); }} />}
      {openId && <PersonPanel personId={openId} range={range} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
